import { Checkbox, Radio, RadioGroup, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { components } from "@certn/api/generated-schemas/client-portal";

import { useOrderContext } from "../../../pages/order/OrderContext";
import { CheckCardError } from "../../../pages/order/steps/components/CheckCardError";
import { CheckCardSelect } from "./check-card-select/CheckCardSelect";
import { CheckCardMultiSelect } from "./check-card-select/CheckCardMultiSelect";
import { ReferenceQuantitySelects } from "./check-card-select/ReferenceQuantitySelects";
import { useCheckCardContext } from "../CheckCardContext";
import { OrderingUmbrellaSelectOption } from "../types";

import { useFlags } from "launchdarkly-react-client-sdk";
import { AddOnCheckboxes } from "./AddOnCheckbox";
import isNil from "lodash/isNil";

const CHECKS_WITH_INCLUDE_PREVIOUS_NAMES_BASE = [
  "BANKRUPTCY_RECORD_CHECK_1",
  "DIRECTORSHIP_RECORD_CHECK_1",
  "DISQUALIFICATION_DISCIPLINARY_1",
];

/**
 * Converts the new SelectFieldInformation format to the legacy selectFieldDescription format.
 *
 * New format (SelectFieldInformation):
 * {
 *   selection_description_label: string,
 *   selection_description: string,
 *   selection_popover_content: {
 *     title: string,
 *     heading?: string,
 *     content: string,
 *     button_link?: { text: string, link: string }
 *   }
 * }
 *
 * Legacy format (selectFieldDescription):
 * [
 *   [label, popoverHeading, popoverContent],
 *   [actionText, actionLink],
 *   [infoText]
 * ]
 */
export function convertSelectFieldInformationToLegacyFormat(
  selectFieldInfo: components["schemas"]["SelectFieldInformation"]
): string[][] {
  const result: string[][] = [];
  const popoverContent = selectFieldInfo.selection_popover_content;

  // First array: [label, popoverHeading, popoverContent]
  const firstArray: string[] = [selectFieldInfo.selection_description_label];

  if (popoverContent) {
    // Use heading if available, otherwise use title
    const heading = popoverContent.heading || popoverContent.title;
    if (heading) {
      firstArray.push(heading);
      // Always add content when we have a heading/title to maintain consistent array structure
      // SelectFieldDescription checks for both [0][1] and [0][2], so we need both present
      firstArray.push(popoverContent.content || "");
    }
  }

  result.push(firstArray);

  // Second array: [actionText, actionLink] (optional)
  // Always create [1] if we have info text, even if button_link doesn't exist
  // This maintains consistency with backend format where info text is always at [2]
  if (popoverContent?.button_link) {
    result.push([
      popoverContent.button_link.text,
      popoverContent.button_link.link,
    ]);
  } else if (selectFieldInfo.selection_description) {
    // Insert empty array at [1] as placeholder when button_link doesn't exist
    // but selection_description does, to ensure info text goes to [2]
    result.push([]);
  }

  // Third array: [infoText] (optional)
  // The selection_description serves as additional info text below the select
  // Backend format always uses [2] for info text, so we maintain that consistency
  if (selectFieldInfo.selection_description) {
    result.push([selectFieldInfo.selection_description]);
  }

  return result;
}

/**
 * Generic function to find the matching radio option by comparing selection_parameters
 * against the saved check_arguments.
 *
 * This works for all check types because selection_parameters is guaranteed to be unique
 * within each check type's select_field_information array.
 */
export function findMatchingRadioOption(
  selectFieldInfo:
    | components["schemas"]["SelectFieldInformationGroup"][]
    | undefined,
  args: Record<string, unknown> | undefined
): components["schemas"]["SelectFieldInformationGroup"] | undefined {
  if (!selectFieldInfo || !args || Object.keys(args).length === 0) {
    return undefined;
  }

  return selectFieldInfo.find((option) => {
    // Guard against missing selection_parameters
    if (!option.selection_parameters) return false;
    // Check if all keys in selection_parameters exist and match in check_arguments
    return Object.entries(option.selection_parameters).every(
      ([key, value]) => args[key] === value
    );
  });
}

export function CheckCardContent() {
  const {
    clientPortalUseNewOrderingCheckCardLogic,
    enableAddressBasedInternationalCriminalRecordCheckOrdering,
    enableAddressBasedBankruptcyCheckOrdering,
    enableAddressBasedDirectorshipCheckOrdering,
    enableAddressBasedCreditCheckOrdering,
    enableCivilLitigationRecordCheck,
  } = useFlags();
  const {
    type,
    selectOptions,
    turnaroundTime,
    description,
    selectFieldDescription: contextSelectFieldDescription,
    selectFieldEndpoint: contextSelectFieldEndpoint,
    isCardSelected,
    onUpdateSelection,
    handleUpdateCheckAddOns,
    customerGroup,
    selectedOptions,
    isDisabled,
    errors,
    currency,
    selectFieldInformation,
    checkArguments,
    isApplicantPay,
    isQuickscreen,
    checkChildren,
    priceIsCatalogMinimum,
    addOnOptions,
  } = useCheckCardContext();

  const { setCheckValidationError, setOrder } = useOrderContext();
  const { t } = useTranslation();

  const isAddressBasedDisabledByType: Partial<Record<string, boolean>> = {
    CRIMINAL_RECORD_REPORT_1:
      !enableAddressBasedInternationalCriminalRecordCheckOrdering ||
      isQuickscreen,
    BANKRUPTCY_RECORD_CHECK_1: !enableAddressBasedBankruptcyCheckOrdering,
    DIRECTORSHIP_RECORD_CHECK_1: !enableAddressBasedDirectorshipCheckOrdering,
    CREDIT_REPORT_1: !enableAddressBasedCreditCheckOrdering,
  };
  const isAddressBasedDisabled = isAddressBasedDisabledByType[type] ?? false;

  const countryBasedRadioValue = selectFieldInformation?.find(
    (info) => info.selection_type === "MULTI"
  )?.radio_button_value;

  // Determine initial radio value from check_arguments using selection_parameters matching
  const getInitialRadioValue = (): string => {
    if (!selectFieldInformation || selectFieldInformation.length === 0) {
      return "";
    }

    if (isAddressBasedDisabled && countryBasedRadioValue) {
      return countryBasedRadioValue;
    }

    // Generic approach: match selection_parameters against check_arguments
    const matchingOption = findMatchingRadioOption(
      selectFieldInformation,
      checkArguments
    );

    if (matchingOption) {
      return matchingOption.radio_button_value;
    }

    // Default to first option
    return selectFieldInformation[0].radio_button_value;
  };

  const [selectedRadioValue, setSelectedRadioValue] =
    useState<string>(getInitialRadioValue);

  // Update radio value when checkArguments changes (e.g., when bundle data loads).
  // When address-based ordering is disabled, clamp to country-based (MULTI) option
  // so that bundle data cannot re-enable an unsupported address-based mode.
  useEffect(() => {
    if (isAddressBasedDisabled && countryBasedRadioValue) {
      setSelectedRadioValue(countryBasedRadioValue);
      return;
    }

    if (checkArguments && Object.keys(checkArguments).length > 0) {
      const matchingOption = findMatchingRadioOption(
        selectFieldInformation,
        checkArguments
      );
      if (
        matchingOption &&
        matchingOption.radio_button_value !== selectedRadioValue
      ) {
        setSelectedRadioValue(matchingOption.radio_button_value);
      }
    }
  }, [
    checkArguments,
    selectFieldInformation,
    selectedRadioValue,
    isAddressBasedDisabled,
    countryBasedRadioValue,
  ]);

  const activeSelectFieldInformation =
    selectFieldInformation?.find(
      (information) => information.radio_button_value === selectedRadioValue
    ) || selectFieldInformation?.[0];

  const isAddressBasedEnabled =
    (type === "CRIMINAL_RECORD_REPORT_1" &&
      enableAddressBasedInternationalCriminalRecordCheckOrdering &&
      !isQuickscreen) ||
    (type === "BANKRUPTCY_RECORD_CHECK_1" &&
      enableAddressBasedBankruptcyCheckOrdering) ||
    (type === "DIRECTORSHIP_RECORD_CHECK_1" &&
      enableAddressBasedDirectorshipCheckOrdering) ||
    (type === "CREDIT_REPORT_1" && enableAddressBasedCreditCheckOrdering) ||
    (type === "CIVIL_LITIGATION_RECORD_CHECK_1" &&
      enableCivilLitigationRecordCheck);

  const selectFieldEndpoint =
    isAddressBasedEnabled && activeSelectFieldInformation?.selection_endpoint
      ? activeSelectFieldInformation.selection_endpoint
      : contextSelectFieldEndpoint;

  // Convert new SelectFieldInformation format to legacy selectFieldDescription format
  const selectFieldDescription = activeSelectFieldInformation
    ? convertSelectFieldInformationToLegacyFormat(activeSelectFieldInformation)
    : contextSelectFieldDescription;

  const handleRadioChange = (value: string) => {
    setSelectedRadioValue(value);
    const newSelectFieldInfo = selectFieldInformation?.find(
      (info) => info.radio_button_value === value
    );

    if (newSelectFieldInfo?.selection_parameters) {
      wrappedOnUpdateSelection(
        { ...newSelectFieldInfo.selection_parameters },
        []
      );
    }
  };

  useEffect(() => {
    const currentOrderingErrorKey = `${type}_ordering_error`;

    if (selectedOptions) {
      const hasOrderingError = selectedOptions.some(
        (option) =>
          option.option_has_ordering_error_for_customer_group?.type === "error"
      );
      setCheckValidationError(currentOrderingErrorKey, hasOrderingError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, type]);

  // Cleanup: clear validation error when component unmounts (check is deselected)
  useEffect(() => {
    const currentOrderingErrorKey = `${type}_ordering_error`;
    return () => {
      setCheckValidationError(currentOrderingErrorKey, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // "Include previous names" checkbox for bankruptcy/directorship/D&D (and credit when flag is on)
  const checksWithIncludePreviousNames = [
    ...CHECKS_WITH_INCLUDE_PREVIOUS_NAMES_BASE,
    ...(enableAddressBasedCreditCheckOrdering ? ["CREDIT_REPORT_1"] : []),
    ...(enableCivilLitigationRecordCheck
      ? ["CIVIL_LITIGATION_RECORD_CHECK_1"]
      : []),
  ];
  const showIncludePreviousNames =
    isCardSelected && checksWithIncludePreviousNames.includes(type);

  const [includePreviousNames, setIncludePreviousNames] = useState<boolean>(
    () => (checkArguments?.include_previous_names as boolean) ?? false
  );

  // Track the latest arguments passed through onUpdateSelection so checkbox
  // changes can re-emit the current payload with the updated value.
  const latestArgsRef = useRef<Record<string, unknown>>(checkArguments ?? {});

  // Wraps onUpdateSelection to inject include_previous_names for relevant check types
  const wrappedOnUpdateSelection = useCallback(
    (
      args: Record<string, unknown>,
      selectedOpts?: OrderingUmbrellaSelectOption[]
    ) => {
      const finalArgs = showIncludePreviousNames
        ? { ...args, include_previous_names: includePreviousNames }
        : args;

      latestArgsRef.current = finalArgs;
      onUpdateSelection?.(finalArgs, selectedOpts);

      // Reset CV Review extraction when selected checks change
      setOrder((prev) =>
        isNil(prev.cv_review_id) ? prev : { ...prev, cv_review_id: null }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onUpdateSelection, includePreviousNames, showIncludePreviousNames]
  );

  const handleIncludePreviousNamesChange = (checked: boolean) => {
    setIncludePreviousNames(checked);
    const updatedArgs = {
      ...latestArgsRef.current,
      include_previous_names: checked,
    };
    latestArgsRef.current = updatedArgs;
    onUpdateSelection?.(updatedArgs, selectedOptions);
  };

  const handleAddOnOptionChange = (key: string, checked: boolean) => {
    const updatedArgs = { ...latestArgsRef.current, [key]: checked };
    latestArgsRef.current = updatedArgs;
    handleUpdateCheckAddOns?.(updatedArgs);

    if (key === "include_cv_review" && !checked) {
      setOrder((prev) => ({
        ...prev,
        cv_review_id: null,
      }));
    }
  };

  const showRadios =
    isCardSelected &&
    clientPortalUseNewOrderingCheckCardLogic &&
    isAddressBasedEnabled &&
    Array.isArray(selectFieldInformation) &&
    selectFieldInformation.length > 1;

  // When inside the address-based grey box, strip the info text (element [2])
  // since the context is already clear from the container layout.
  const effectiveSelectFieldDescription =
    showRadios && selectFieldDescription
      ? selectFieldDescription.slice(0, 2)
      : selectFieldDescription;

  const selectComponent = () => {
    if (!isCardSelected) {
      return null;
    }

    if (type === "REFERENCE_1") {
      return (
        <ReferenceQuantitySelects
          selectFieldDescription={contextSelectFieldDescription}
          selectFieldEndpoint={selectFieldEndpoint}
          onUpdateSelection={wrappedOnUpdateSelection}
          isDisabled={isDisabled}
          checkArguments={checkArguments}
          checkChildren={checkChildren}
          customerGroup={customerGroup}
        />
      );
    }

    const isCreditCheck = type === "CREDIT_REPORT_1";
    // Credit flags are off, show the legacy single-select (Canada/US).
    // When API returns selected_options but no select_options/endpoint (e.g. legacy payload),
    // use selectedOptions as options so the dropdown shows the current selection and Next stays enabled.
    if (isCreditCheck && !enableAddressBasedCreditCheckOrdering) {
      const legacyCreditSelectOptions =
        (selectOptions?.length ? selectOptions : null) ?? selectedOptions ?? [];
      return (
        <CheckCardSelect
          currency={currency}
          selectFieldDescription={effectiveSelectFieldDescription}
          selectFieldEndpoint={selectFieldEndpoint}
          onUpdateSelection={wrappedOnUpdateSelection}
          selectOptions={legacyCreditSelectOptions}
          type={type}
          customerGroup={customerGroup}
          selectedOptions={selectedOptions}
          isDisabled={isDisabled}
          checkArguments={checkArguments}
          selectFieldInformation={activeSelectFieldInformation}
          isApplicantPay={isApplicantPay}
          priceIsCatalogMinimum={priceIsCatalogMinimum}
          checkChildren={checkChildren}
        />
      );
    }

    // Use CheckCardMultiSelect for MULTI selection type
    if (
      clientPortalUseNewOrderingCheckCardLogic &&
      activeSelectFieldInformation &&
      activeSelectFieldInformation.selection_type === "MULTI"
    ) {
      return (
        <CheckCardMultiSelect
          currency={currency}
          selectFieldDescription={effectiveSelectFieldDescription}
          onUpdateSelection={wrappedOnUpdateSelection}
          selectOptions={selectOptions}
          type={type}
          customerGroup={customerGroup}
          selectedOptions={selectedOptions}
          isDisabled={isDisabled}
          selectFieldInformation={activeSelectFieldInformation}
          isApplicantPay={isApplicantPay}
          checkArguments={checkArguments}
          isQuickscreen={isQuickscreen}
          priceIsCatalogMinimum={priceIsCatalogMinimum}
          checkChildren={checkChildren}
        />
      );
    }

    // Use CheckCardSelect for SINGLE selection type or legacy format
    if (selectFieldEndpoint || !!selectOptions?.length) {
      return (
        <CheckCardSelect
          currency={currency}
          selectFieldDescription={effectiveSelectFieldDescription}
          selectFieldEndpoint={selectFieldEndpoint}
          onUpdateSelection={wrappedOnUpdateSelection}
          selectOptions={selectOptions}
          type={type}
          customerGroup={customerGroup}
          selectedOptions={selectedOptions}
          isDisabled={isDisabled}
          checkArguments={checkArguments}
          selectFieldInformation={activeSelectFieldInformation}
          isApplicantPay={isApplicantPay}
          priceIsCatalogMinimum={priceIsCatalogMinimum}
          checkChildren={checkChildren}
        />
      );
    }

    return null;
  };

  return (
    <>
      <div className={styles.middleSection}>
        <Text style="global/info-label">
          {t("Est turnaround time")}: {turnaroundTime}
        </Text>
        <Text
          style="global/body-primary"
          color="text-weak"
          className={styles.description}
        >
          {description}
        </Text>
        {selectedOptions &&
          selectedOptions.map((selectedOption) => (
            <CheckCardError
              key={selectedOption.label}
              error={
                selectedOption.option_has_ordering_error_for_customer_group
              }
            />
          ))}
        {errors}
      </div>
      {showRadios && (
        <div className={styles.addressBasedContainer}>
          <div className={styles.addressBasedInnerSection}>
            <RadioGroup
              value={selectedRadioValue}
              onChange={handleRadioChange}
              orientation="horizontal"
              aria-label={t("Select check type")}
              isDisabled={isDisabled}
            >
              {selectFieldInformation.map((information) => (
                <Radio
                  key={information.radio_button_value}
                  value={information.radio_button_value}
                  testID={information.radio_button_value}
                >
                  {information.radio_button_label}
                </Radio>
              ))}
            </RadioGroup>
          </div>
          {selectComponent()}
          {showIncludePreviousNames && (
            <div className={styles.addressBasedInnerSection}>
              <Checkbox
                testID="include-previous-names"
                isSelected={includePreviousNames}
                onChange={handleIncludePreviousNamesChange}
                isDisabled={isDisabled}
              >
                {t("Include previous names")}
              </Checkbox>
              <div className={styles.checkboxSubtext}>
                <Text style="global/body-secondary" color="text-weak">
                  {t(
                    "Searches will be run on the applicant's current name and any previously used names, where applicable, in relevant countries."
                  )}
                </Text>
              </div>
            </div>
          )}
        </div>
      )}
      {!showRadios && showIncludePreviousNames && (
        <div
          className={styles.addressBasedContainer}
          onClick={(event) => {
            // D&D has no radio group so it never enters the box above.
            // Helper text sits outside the AriaCheckbox; without this the
            // card wrapper treats the click as deselect.
            event.stopPropagation();
          }}
        >
          {selectComponent()}
          <div className={styles.addressBasedInnerSection}>
            <Checkbox
              testID="include-previous-names"
              isSelected={includePreviousNames}
              onChange={handleIncludePreviousNamesChange}
              isDisabled={isDisabled}
            >
              {t("Include previous names")}
            </Checkbox>
            <div className={styles.checkboxSubtext}>
              <Text style="global/body-secondary" color="text-weak">
                {t(
                  "Searches will be run on the applicant's current name and any previously used names, where applicable, in relevant countries."
                )}
              </Text>
            </div>
          </div>
        </div>
      )}
      {!showRadios && !showIncludePreviousNames && selectComponent()}
      {isCardSelected && addOnOptions && addOnOptions.length > 0 && (
        <AddOnCheckboxes
          isQuickscreen={isQuickscreen || false}
          addOnOptions={addOnOptions}
          checkArguments={checkArguments}
          isDisabled={isDisabled}
          currency={currency}
          handleAddOnOptionChange={handleAddOnOptionChange}
        />
      )}
    </>
  );
}

const styles = {
  middleSection: css({
    display: "flex",
    flexDirection: "column",
    padding: `0 ${"var(--space-4)"}`,
    gap: "var(--space-2)",
    marginBottom: "var(--space-2)",
  }),
  description: css({
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: "2",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  addressBasedContainer: css({
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--background-accent-neutral-weakest)",
    margin: "var(--space-2) var(--space-05) var(--space-0) var(--space-05)",
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-3)",
    // Fix for duplicated rendering from <MediaQuery> using "display: none".
    // See https://github.com/Certn/certn-apps/pull/4543#discussion_r2612339166
    contain: "layout style",
    isolation: "isolate",
  }),
  addressBasedInnerSection: css({
    padding: "var(--space-2) var(--space-3)",
    margin: "var(--space-0) var(--space-05)",
  }),
  checkboxSubtext: css({
    marginLeft: "28px",
  }),
};
